import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { BookOpen, Mail, Lock, Shield, Eye, EyeOff, CheckCircle2, XCircle, Smartphone } from "lucide-react";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";

const PW_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "One number", test: (p) => /\d/.test(p) },
  { label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function PasswordField({ value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pl-10 pr-10"
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function ContentManagerPortal() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const token = params.get("token");

  // mode: "activate" | "login"
  const [mode, setMode] = useState(token ? "activate" : "login");

  // Activation state
  const [tokenValid, setTokenValid] = useState(null); // null=checking, true/false
  const [activateEmail, setActivateEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [activating, setActivating] = useState(false);

  // Login state
  const [email, setEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1); // 1 login, 2 otp
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showReset, setShowReset] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1 request, 2 code+new password
  const [resetOtp, setResetOtp] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/admin/content-managers/activate/${token}`);
        const data = await res.json();
        setTokenValid(!!data.valid);
        if (data.valid) {
          setActivateEmail(data.email || "");
          setEmail(data.email || "");
        }
      } catch {
        setTokenValid(false);
      }
    })();
  }, [token]);

  const pwOk = PW_RULES.every((r) => r.test(password));

  const handleActivate = async () => {
    if (!pwOk) {
      toast.error("Password does not meet the requirements");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setActivating(true);
    try {
      const res = await fetch(`${API}/admin/content-managers/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Account activated! Please log in.");
        setParams({}, { replace: true });
        setMode("login");
        setStep(1);
      } else {
        toast.error(data.detail || "Activation failed");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setActivating(false);
    }
  };

  const handleLogin = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!email.trim() || !loginPassword.trim()) {
      toast.error("Enter your email and password");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase(), password: loginPassword, portal: "contentmanager" }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.requires_otp) {
        toast.success("Verification code sent to your email & WhatsApp");
        setStep(2);
      } else if (res.ok && data.success) {
        navigate("/contentmanager/editor");
      } else if (res.status === 401) {
        toast.error(data.detail || "Incorrect password");
      } else if (res.status === 404) {
        toast.error(data.detail || "No account found with this email");
      } else if (res.status === 423) {
        toast.error(data.detail || "Account locked. Contact an administrator.");
      } else {
        toast.error(data.detail || "Login failed");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/auth/verify-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp_code: otp.trim(), portal: "contentmanager" }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success("Login successful!");
        setTimeout(() => navigate("/contentmanager/editor"), 100);
      } else if (res.status === 400) {
        toast.error(data.detail || "Invalid or expired code");
        setStep(1);
        setOtp("");
      } else {
        toast.error(data.detail || "Verification failed");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const requestReset = async () => {
    if (!email.trim()) {
      toast.error("Enter your email first");
      return;
    }
    setResetting(true);
    try {
      const res = await fetch(`${API}/admin/content-managers/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      await res.json().catch(() => ({}));
      toast.success("If an active account exists, a reset code was sent to your email & WhatsApp");
      setResetStep(2);
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setResetting(false);
    }
  };

  const submitReset = async () => {
    if (resetOtp.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    if (!PW_RULES.every((r) => r.test(resetPassword))) {
      toast.error("Password does not meet the requirements");
      return;
    }
    setResetting(true);
    try {
      const res = await fetch(`${API}/admin/content-managers/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp: resetOtp.trim(), new_password: resetPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success("Password reset! Please log in.");
        setShowReset(false);
        setResetStep(1);
        setResetOtp("");
        setResetPassword("");
        setStep(1);
      } else {
        toast.error(data.detail || "Reset failed");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 flex flex-col">
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100">
        <SharedHeader transparent={true} />
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-8">
        <Card className="w-full max-w-md shadow-xl bg-white" data-testid="cm-portal-card">
          <CardContent className="p-6 sm:p-8 space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-8 w-8 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Content Manager</h2>
              <p className="text-gray-600 mt-1">
                {mode === "activate"
                  ? "Set your password to activate your account"
                  : showReset
                  ? "Reset your password"
                  : "Sign in to manage your assigned content"}
              </p>
            </div>

            {mode === "activate" && (
              <div className="space-y-4" data-testid="cm-activate-view">
                {tokenValid === null && <p className="text-center text-sm text-gray-500">Checking your link…</p>}
                {tokenValid === false && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-red-700">This activation link is invalid or has expired.</p>
                    <p className="text-xs text-red-500 mt-1">Ask an administrator to resend your invite.</p>
                  </div>
                )}
                {tokenValid === true && (
                  <>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input value={activateEmail} disabled className="pl-10 bg-gray-50" />
                    </div>
                    <PasswordField value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a password" testId="cm-activate-password" />
                    <PasswordField value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" testId="cm-activate-confirm" />
                    <ul className="space-y-1">
                      {PW_RULES.map((r) => {
                        const ok = r.test(password);
                        return (
                          <li key={r.label} className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-600" : "text-gray-400"}`}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> {r.label}
                          </li>
                        );
                      })}
                    </ul>
                    <Button
                      onClick={handleActivate}
                      disabled={activating || !pwOk || password !== confirm}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      data-testid="cm-activate-btn"
                    >
                      {activating ? "Activating…" : "Activate & Continue"}
                    </Button>
                  </>
                )}
              </div>
            )}

            {mode === "login" && !showReset && step === 1 && (
              <form onSubmit={handleLogin} className="space-y-4" data-testid="cm-login-view">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className="pl-10"
                    data-testid="cm-login-email"
                  />
                </div>
                <PasswordField value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Password" testId="cm-login-password" />
                <Button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="cm-login-btn">
                  {loading ? "Signing in…" : "Continue"}
                </Button>
                <button
                  type="button"
                  onClick={() => { setShowReset(true); setResetStep(1); }}
                  className="w-full text-sm text-indigo-600 hover:text-indigo-700"
                  data-testid="cm-forgot-password-link"
                >
                  Forgot password?
                </button>
              </form>
            )}

            {mode === "login" && showReset && (
              <div className="space-y-4" data-testid="cm-reset-view">
                {resetStep === 1 && (
                  <>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Your email"
                        className="pl-10"
                        data-testid="cm-reset-email"
                      />
                    </div>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-start gap-2">
                      <Smartphone className="h-5 w-5 text-indigo-600 mt-0.5" />
                      <p className="text-sm text-indigo-700">We'll send a reset code to your email and WhatsApp.</p>
                    </div>
                    <Button onClick={requestReset} disabled={resetting} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="cm-reset-request-btn">
                      {resetting ? "Sending…" : "Send reset code"}
                    </Button>
                  </>
                )}
                {resetStep === 2 && (
                  <>
                    <Input
                      value={resetOtp}
                      onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      className="text-center text-3xl tracking-[0.5em] font-mono"
                      maxLength={6}
                      data-testid="cm-reset-otp"
                    />
                    <PasswordField value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="New password" testId="cm-reset-password" />
                    <ul className="space-y-1">
                      {PW_RULES.map((r) => {
                        const ok = r.test(resetPassword);
                        return (
                          <li key={r.label} className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-600" : "text-gray-400"}`}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> {r.label}
                          </li>
                        );
                      })}
                    </ul>
                    <Button onClick={submitReset} disabled={resetting} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="cm-reset-submit-btn">
                      {resetting ? "Resetting…" : "Reset password"}
                    </Button>
                  </>
                )}
                <button
                  onClick={() => { setShowReset(false); setResetStep(1); setResetOtp(""); setResetPassword(""); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                  data-testid="cm-reset-back"
                >
                  Back to login
                </button>
              </div>
            )}

            {mode === "login" && !showReset && step === 2 && (
              <div className="space-y-4" data-testid="cm-otp-view">
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3">
                  <Smartphone className="h-5 w-5 text-indigo-600 mt-0.5" />
                  <p className="text-sm text-indigo-700">
                    We sent a 6-digit code to your <strong>email</strong> and <strong>WhatsApp</strong>. Enter it below.
                  </p>
                </div>
                <Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-3xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  data-testid="cm-otp-input"
                />
                <Button onClick={handleVerify} disabled={loading || otp.length !== 6} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="cm-verify-otp-btn">
                  {loading ? "Verifying…" : "Verify & Sign In"}
                </Button>
                <p className="text-center text-sm text-gray-500">
                  Didn't receive it?{" "}
                  <button onClick={() => handleLogin()} className="text-indigo-600 hover:underline">Resend</button>
                </p>
                <button onClick={() => { setStep(1); setOtp(""); }} className="w-full text-sm text-gray-500 hover:text-gray-700">
                  Back
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <SharedFooter />
    </div>
  );
}
